import { useCallback, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import ReplayIcon from '@mui/icons-material/Replay';
import { ChevronLeft, ChevronRight } from 'react-feather';

import { AILogo, BrainIcon, CopyIcon, Tick } from '../../../../../assets/images';
import StyledTooltip from '../../../../../components/tooltip';
import ChatMarkdown from '../chat-markdown';
import McpWarningBanner from '../mcp-warning-banner';
import ToolsUsedPanel from '../tools-used-panel';
import WebSearchActivity from '../web-search-activity';
import { useTypewriterStream } from '../../hooks/use-typewriter-stream';
import type { DataItem, ToolCall } from '../../../../../utils/interface-types';

export type AssistantMessageProps = {
  messageIndex: number;
  activeMessage: string;
  isStreaming: boolean;
  throttleStreamMarkdown: boolean;
  isThinking: boolean;
  thinkingText?: string;
  thinkingDurationSec?: number;
  thinkingEnabled: boolean;
  thinkingExpanded: boolean;
  onToggleThinking: () => void;
  toolCallsExpanded: boolean;
  onToggleToolCalls: () => void;
  toolCalls?: ToolCall[];
  mcpWarnings?: string[];
  webSearchUsed?: boolean;
  webSearchQuery?: string;
  sources?: DataItem['sources'];
  generatedFilesHtml?: string;
  hasMultipleVersions: boolean;
  selectedVersionIndex: number;
  versionCount: number;
  onSelectNextVersion: () => void;
  onSelectPrevVersion: () => void;
  showResponseActions: boolean;
  isCopied: boolean;
  resCopiedIndex: number | null;
  onCopy: () => void;
  showRetry: boolean;
  onRetry?: () => void;
  onInteractiveClick: (e: MouseEvent) => void;
  onInteractiveKeyDown: (e: KeyboardEvent) => void;
  showAvatar?: boolean;
};

function AssistantMessage({
  messageIndex,
  activeMessage,
  isStreaming,
  throttleStreamMarkdown,
  isThinking,
  thinkingText,
  thinkingDurationSec,
  thinkingEnabled,
  thinkingExpanded,
  onToggleThinking,
  toolCallsExpanded,
  onToggleToolCalls,
  toolCalls,
  mcpWarnings,
  webSearchUsed,
  webSearchQuery = undefined,
  sources,
  generatedFilesHtml,
  hasMultipleVersions,
  selectedVersionIndex,
  versionCount,
  onSelectPrevVersion,
  onSelectNextVersion,
  showResponseActions,
  isCopied,
  resCopiedIndex,
  onCopy,
  showRetry,
  onRetry,
  onInteractiveClick,
  onInteractiveKeyDown,
  showAvatar = true,
}: AssistantMessageProps) {
  const hasResponseText = Boolean(activeMessage?.trim());
  const hasSources = Boolean(sources && sources.length > 0);
  const webSearchToolLoading = Boolean(
    toolCalls?.some((toolCall) => toolCall.tool_name === 'web_search' && toolCall.is_loading),
  );
  const isWebSearching = Boolean(
    isStreaming &&
      !hasResponseText &&
      (webSearchQuery || webSearchToolLoading || webSearchUsed || hasSources),
  );
  const showWebSearchActivity = isWebSearching || hasSources || Boolean(webSearchUsed);
  const otherToolCalls = (toolCalls || []).filter(
    (toolCall) => toolCall.tool_name !== 'web_search',
  );
  const hasToolActivity = otherToolCalls.length > 0;
  const isWaitingForFirstStreamChunk =
    isStreaming &&
    !hasResponseText &&
    !showWebSearchActivity &&
    !hasToolActivity &&
    !(isThinking && thinkingEnabled);
  const hasMcpWarnings = Boolean(mcpWarnings && mcpWarnings.length > 0);
  const typewriterActive = throttleStreamMarkdown && isStreaming;
  const onStreamReveal = useCallback(() => {
    window.dispatchEvent(new CustomEvent('grigo-stream-layout-tick'));
  }, []);
  const { displayText, isRevealing } = useTypewriterStream(activeMessage, typewriterActive, {
    onReveal: onStreamReveal,
  });
  const markdownContent = typewriterActive ? displayText : activeMessage;
  const showStreamCursor =
    isStreaming &&
    Boolean(markdownContent?.trim()) &&
    (isRevealing || markdownContent.length < activeMessage.length);

  const showActions = showResponseActions && !isThinking && hasResponseText;
  const hasThoughts = Boolean(thinkingText?.trim());
  const showThinkingUi = hasThoughts || (isThinking && thinkingEnabled);
  const thoughtsVisible = Boolean(thinkingExpanded && hasThoughts);
  let thinkingLabel = 'Thought process';
  if (isThinking) {
    thinkingLabel = 'Thinking';
  } else if (thinkingDurationSec && thinkingDurationSec > 0) {
    thinkingLabel =
      thinkingDurationSec === 1
        ? 'Thought for 1 second'
        : `Thought for ${thinkingDurationSec} seconds`;
  }
  const showMetaAboveResponse = !hasResponseText;
  const toolsCompact = hasResponseText;

  const renderThinkingBlock = (): ReactNode => {
    if (!showThinkingUi) {
      return null;
    }

    return (
      <div className="thinking-container mb-3">
        <div
          className={`group flex items-center gap-2 cursor-pointer rounded-md px-1 py-1 transition-colors hover:bg-zinc-50 ${
            thoughtsVisible ? 'text-[#0A8BC7]' : 'text-zinc-500'
          }`}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            if (hasThoughts) onToggleThinking();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              if (hasThoughts) onToggleThinking();
            }
          }}
        >
          <img
            src={BrainIcon}
            alt=""
            className="brain-icon w-3.5 h-3.5 opacity-70"
            style={{
              filter: thoughtsVisible
                ? 'brightness(0) saturate(100%) invert(42%) sepia(68%) saturate(1200%) hue-rotate(162deg) brightness(92%) contrast(95%)'
                : 'brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(89%)',
            }}
          />
          <span
            className={`text-[13px] font-medium ${
              isThinking ? 'chat-thinking-shimmer' : 'text-zinc-500 group-hover:text-zinc-700'
            }`}
          >
            {thinkingLabel}
          </span>
          {hasThoughts ? (
            <svg
              className={`w-3.5 h-3.5 transition-transform duration-200 ${
                thoughtsVisible ? 'rotate-180 text-[#0A8BC7]' : 'text-zinc-400'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          ) : null}
        </div>
        {thoughtsVisible ? (
          <div className="mt-1.5 pl-3 text-[13px] leading-5 text-zinc-500 whitespace-pre-wrap border-l-2 border-l-zinc-200">
            {thinkingText}
            {isThinking ? (
              <span className="inline-block w-0.5 h-3.5 bg-[#0A8BC7] ml-1 animate-pulse align-middle" />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderToolsBlock = (): ReactNode => {
    if (!otherToolCalls.length) {
      return null;
    }
    return (
      <ToolsUsedPanel
        toolCalls={otherToolCalls}
        expanded={toolCallsExpanded}
        onToggle={onToggleToolCalls}
        compact={toolsCompact}
      />
    );
  };

  const renderWebSearchBlock = (compact: boolean): ReactNode => {
    if (!showWebSearchActivity) {
      return null;
    }
    return (
      <WebSearchActivity
        isSearching={isWebSearching}
        query={webSearchQuery}
        sources={sources}
        compact={compact}
      />
    );
  };

  const renderVersionNav = (): ReactNode => {
    if (!hasMultipleVersions) {
      return null;
    }
    return (
      <div className="response-version-nav flex items-center gap-3 mb-3 pb-2 border-b border-zinc-100">
        <button
          type="button"
          className="response-version-nav-btn flex items-center justify-center w-7 h-7 rounded-full border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
          disabled={selectedVersionIndex <= 0}
          onClick={(e) => {
            e.stopPropagation();
            onSelectPrevVersion();
          }}
          aria-label="Previous response"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="text-xs font-medium text-zinc-500 whitespace-nowrap">
          Response {selectedVersionIndex + 1} of {versionCount}
        </span>
        <button
          type="button"
          className="response-version-nav-btn flex items-center justify-center w-7 h-7 rounded-full border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-40"
          disabled={selectedVersionIndex >= versionCount - 1}
          onClick={(e) => {
            e.stopPropagation();
            onSelectNextVersion();
          }}
          aria-label="Next response"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  return (
    <div className="response-container w-full group">
      <div className="chat-assistant-row flex gap-2 items-start w-full">
        {showAvatar ? (
          <div className="chat-assistant-avatar">
            <img src={AILogo} alt="Grigo" width={16} height={16} />
          </div>
        ) : (
          <div className="chat-assistant-avatar-spacer" aria-hidden />
        )}
        <div className="flex-1 min-w-0">
          <div
            role="button"
            tabIndex={0}
            aria-label="Chat response with interactive content"
            onClick={onInteractiveClick}
            onKeyDown={onInteractiveKeyDown}
            className="chat-assistant-body response-span min-w-0"
            style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
          >
            {hasMcpWarnings && mcpWarnings ? <McpWarningBanner warnings={mcpWarnings} /> : null}

            {renderThinkingBlock()}
            {isStreaming ? renderWebSearchBlock(false) : null}
            {showMetaAboveResponse ? renderToolsBlock() : null}

            {isWaitingForFirstStreamChunk ? (
              <div className="chat-stream-shimmer my-2" aria-label="Generating response" />
            ) : null}

            {hasResponseText ? renderVersionNav() : null}

            {hasResponseText ? (
              <>
                <ChatMarkdown
                  content={markdownContent}
                  isStreaming={showStreamCursor}
                  isStreamAnimating={isStreaming}
                  className="prose prose-sm max-w-none chat-markdown-root"
                />
                {!isStreaming ? renderWebSearchBlock(true) : null}
                {generatedFilesHtml ? (
                  <div
                    className="mt-2"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(generatedFilesHtml, {
                        ALLOWED_TAGS: ['br', 'div', 'h4', 'span', 'button'],
                        ALLOWED_ATTR: [
                          'style',
                          'class',
                          'data-url',
                          'data-filename',
                          'data-container',
                          'data-fileid',
                        ],
                      }),
                    }}
                  />
                ) : null}
                {renderToolsBlock()}
              </>
            ) : null}
          </div>

          {(showActions || (showRetry && onRetry)) && (
            <div className="chat-response-actions flex items-center gap-2 mt-2 pl-0.5">
              {showActions ? (
                <button
                  type="button"
                  className="response-actions text-zinc-400 hover:text-zinc-600 p-1 rounded-md hover:bg-zinc-100"
                  onClick={onCopy}
                  aria-label="Copy to clipboard"
                >
                  <img
                    src={!isCopied || resCopiedIndex !== messageIndex ? CopyIcon : Tick}
                    alt="copy"
                    style={{ width: 17, height: 17 }}
                  />
                </button>
              ) : null}
              {showRetry && onRetry ? (
                <StyledTooltip title="Retry">
                  <button
                    type="button"
                    className="response-actions text-zinc-400 hover:text-zinc-600 p-1 rounded-md hover:bg-zinc-100"
                    onClick={onRetry}
                    aria-label="Retry"
                  >
                    <ReplayIcon sx={{ fontSize: 17 }} />
                  </button>
                </StyledTooltip>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

AssistantMessage.defaultProps = {
  thinkingText: undefined,
  thinkingDurationSec: undefined,
  toolCalls: undefined,
  mcpWarnings: undefined,
  webSearchUsed: undefined,
  webSearchQuery: undefined,
  sources: undefined,
  generatedFilesHtml: undefined,
  onRetry: undefined,
  showAvatar: true,
};

export default AssistantMessage;
